import { ArrowRight, CheckCircle2, ClipboardCheck, FileSearch, Gauge, Network, SearchCheck } from "lucide-react";
import { Link } from "wouter";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SEOHead from "@/components/SEOHead";
import { trackCalendlyClick, trackSectionCtaClick } from "@/lib/analytics";
import { getServiceSchemaSupportingEntities } from "@/lib/siteEntityGraph";

import SchemaScript from "@/components/SchemaScript";
const CANONICAL = "https://integrated.social/services/ai-citation-service/";
const INTRODUCTORY_RATE_END = "2026-12-28";

const faqs = [
  {
    question: "Can you guarantee that ChatGPT or Gemini will cite my business?",
    answer: "No. No agency can guarantee a citation, ranking, traffic, lead or revenue outcome from a third-party AI system. ChatGPT, Gemini and Google change their retrieval, ranking and response systems without notice. This service improves the clarity, evidence, technical accessibility and entity signals that make your pages more useful candidates to be cited, then measures what changes on an agreed query set.",
  },
  {
    question: "What does the £750 AI Citation Audit include?",
    answer: "The one-off, senior-led audit starts from £750 + VAT. It reviews an agreed set of commercial questions, the current cited-source landscape where it can be observed, priority page and content gaps, technical crawl and schema signals, entity evidence and practical remediation order. You receive a scoped recommendation before deciding whether ongoing work is appropriate.",
  },
  {
    question: "What does the £2,200 monthly AI citation retainer include?",
    answer: "The introductory retainer starts from £2,200 + VAT per month for a defined priority scope. It can include answer-first page work, structured data and technical remediation, source and evidence improvement, internal linking, and a documented measurement cadence. The precise volume, owners and exclusions are agreed after the audit; it is not an unlimited-content package.",
  },
  {
    question: "How does the 29% introductory discount work?",
    answer: "For new AI citation service retainers confirmed by 28 December 2026, the monthly starting rate is from £2,200 + VAT and is presented as a 29% introductory discount. The offer applies to the agreed citation-service scope only. We confirm the applicable term, scope, VAT treatment and rate after the offer period in writing before work begins.",
  },
  {
    question: "Which AI answers does the service cover?",
    answer: "The work is designed for buyer questions that may surface in ChatGPT, Gemini and Google AI experiences, alongside the SEO foundations those systems may draw on. We do not claim that every platform uses the same data sources or signals. The audit selects the answer surfaces and priority questions that are commercially relevant to your audience.",
  },
  {
    question: "How do you measure whether the work is helping?",
    answer: "We define a documented query set, record the market, prompt context and source evidence available at the time, then compare changes over time. We combine that evidence with technical crawlability, page quality, Search Console and analytics signals where they are available. A single ad-hoc chatbot response is not treated as proof of sustainable visibility.",
  },
  {
    question: "Can an AI visibility tool prove brand reputation or return on investment?",
    answer: "No. A tool can organize observations such as visible mentions, cited sources, referral activity, and changes in a defined question set, but it cannot prove reputation, buyer preference, or commercial impact on its own. Review those observations with their date, context, source evidence, technical signals, and qualified commercial feedback.",
  },
  {
    question: "Who is this service for?",
    answer: "It is built for B2B, selected B2C and ecommerce teams whose buyers ask complex comparison, category or implementation questions before they enquire. It is most useful when there is a real offer, credible evidence to publish and an internal owner who can approve subject-matter input. It is not a shortcut for a new site with no useful source material.",
  },
] as const;

const workstreams = [
  {
    icon: SearchCheck,
    title: "Citation and query baseline",
    body: "We agree the buyer questions, markets and answer surfaces that matter, then document the current evidence before changing pages.",
  },
  {
    icon: FileSearch,
    title: "Source-ready pages",
    body: "We improve the pages a buyer or system should understand first: direct answers, qualified claims, clear authorship, source context and useful internal routes.",
  },
  {
    icon: Network,
    title: "Technical and entity clarity",
    body: "We review crawlability, canonical delivery, page semantics, structured data and the consistency of your organisation, services and proof across the site.",
  },
  {
    icon: Gauge,
    title: "Evidence-led iteration",
    body: "We retain a query log, inspect what can be observed, prioritise the next useful improvement and report the limits as plainly as the progress.",
  },
] as const;

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    ...getServiceSchemaSupportingEntities(),
    {
      "@type": "WebPage",
      "@id": CANONICAL,
      url: CANONICAL,
      name: "Improve your chances of being cited by AI",
      headline: "Improve your chances of being cited by AI",
      description: "Evidence-led AI citation service for teams that want clearer, more source-ready pages for ChatGPT, Gemini and Google AI experiences. Starts with an audit from £750, followed by a defined monthly scope from £2,200 during the limited introductory offer.",
      isPartOf: { "@id": "https://integrated.social/#website" },
      about: { "@id": `${CANONICAL}#service` },
      author: { "@id": "https://integrated.social/modi-elnadi/#person" },
      breadcrumb: { "@id": `${CANONICAL}#breadcrumb` },
      dateModified: "2026-10-30",
      inLanguage: "en-US",
    },
    {
      "@type": "Service",
      "@id": `${CANONICAL}#service`,
      name: "AI Citation Service for ChatGPT and Gemini",
      serviceType: "AI citation readiness, answer engine optimisation and AI search visibility",
      description: "A senior-led, evidence-led service that improves answer-first content, source context, structured data, entity clarity and measurement for commercially relevant AI-search questions. Citations are not guaranteed.",
      provider: { "@id": "https://integrated.social/#organization" },
      areaServed: [
        { "@type": "Country", name: "United Kingdom" },
        { "@type": "Country", name: "United States" },
        { "@type": "Place", name: "Global" },
      ],
      audience: { "@type": "BusinessAudience", audienceType: "B2B, selected B2C and ecommerce teams" },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "AI Citation Service starting points",
        itemListElement: [
          {
            "@type": "Offer",
            name: "AI Citation Audit",
            priceCurrency: "GBP",
            price: "750",
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              price: "750",
              priceCurrency: "GBP",
              unitText: "one-off audit",
              valueAddedTaxIncluded: false,
            },
            itemOffered: { "@type": "Service", name: "AI Citation Audit" },
          },
          {
            "@type": "Offer",
            name: "AI Citation Service introductory retainer",
            priceCurrency: "GBP",
            price: "2200",
            priceValidUntil: INTRODUCTORY_RATE_END,
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              price: "2200",
              priceCurrency: "GBP",
              unitText: "per month",
              valueAddedTaxIncluded: false,
            },
            itemOffered: { "@type": "Service", name: "AI Citation Service monthly retainer" },
          },
        ],
      },
    },
    {
      "@type": "FAQPage",
      "@id": `${CANONICAL}#faq`,
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://integrated.social/" },
        { "@type": "ListItem", position: 2, name: "Services", item: "https://integrated.social/services/" },
        { "@type": "ListItem", position: 3, name: "Improve your chances of being cited by AI", item: CANONICAL },
      ],
    },
  ],
};

export default function ServiceAICitation() {
  const bookCall = () => {
    trackCalendlyClick("ai_citation_service");
    trackSectionCtaClick({
      section_name: "ai_citation_service",
      cta_text: "Book a 30-minute call",
      destination: "https://calendly.com/modielnadi/30min",
    });
  };

  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <SEOHead
        title="AI Citation Service | Evidence-Led AI Visibility, UK"
        description="Evidence-led AI citation readiness for ChatGPT, Gemini and Google AI. Start with a senior-led audit from £750, then a defined monthly scope."
        canonical={CANONICAL}
      />
      <SchemaScript schema={JSON.stringify(schema)} />
      <Header />
      <main>
        <div className="container px-4 pt-24">
          <Breadcrumbs includeSchema={false} items={[{ label: "Services", href: "/services/" }, { label: "Improve your chances of being cited by AI" }]} />
        </div>

        <header className="container grid gap-12 px-4 pb-16 pt-14 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pt-20">
          <div className="max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF8C00]">AI citation service</p>
            <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">Improve your chances of being cited by AI</h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/75 md:text-xl">
              Build clearer, evidence-led pages that are easier for AI answer systems to assess and cite when your buyers ask relevant questions. We begin with a senior-led audit, then agree a realistic monthly scope around your priority evidence, pages and technical foundations.
            </p>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-white/55">
              We do not promise citations, rankings, traffic, leads or revenue. ChatGPT, Gemini and Google control their own outputs. Our job is to improve your source readiness, document the evidence and keep the work tied to commercial questions. Learn how the monthly work is structured in <Link href="/services/ai-citation-programme/" className="text-[#FFB84D] underline underline-offset-4">the AI Citation Programme</Link>.
            </p>
            <div className="mt-7 max-w-3xl" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_hero" /></div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer" onClick={bookCall} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#FF8C00] px-5 py-3 text-sm font-bold text-[#0F1419] transition-colors hover:bg-[#FFB84D]">
                Book a 30-minute call <ArrowRight className="h-4 w-4" />
              </a>
              <Link href="/free-ai-growth-audit/" className="inline-flex min-h-11 items-center justify-center rounded-lg border border-white/25 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-[#00D4AA] hover:text-[#00D4AA]">
                Check my AI visibility</Link>
            </div>
          </div>
          <aside className="rounded-2xl border border-[#00D4AA]/30 bg-[#00D4AA]/[0.06] p-6 md:p-8" aria-label="AI citation service starting points">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#00D4AA]">Clear starting points</p>
            <div className="mt-6 border-b border-white/10 pb-6">
              <p className="text-sm font-semibold text-white">1. AI Citation Audit</p>
              <p className="mt-2 font-serif text-3xl text-[#FFB84D]">From £750</p>
              <p className="mt-2 text-sm leading-relaxed text-white/65">One-off, senior-led diagnosis of your priority questions, pages, technical signals and evidence gaps. VAT excluded.</p>
            </div>
            <div className="pt-6">
              <p className="text-sm font-semibold text-white">2. Citation service retainer</p>
              <p className="mt-2 font-serif text-3xl text-[#FFB84D]">From £2,200<span className="ml-1 text-base text-white/70">/month + VAT</span></p>
              <p className="mt-2 text-sm leading-relaxed text-white/65">A defined monthly scope for priority source-ready improvements and measurement. New retainers confirmed by <strong className="text-white">28 December 2026</strong> receive the 29% introductory discount.</p>
            </div>
          </aside>
        </header>

        <section className="border-y border-white/10 bg-white/[0.03] py-16">
          <div className="container px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00D4AA]">What the work addresses</p>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl">A source should be useful before it can be selected.</h2>
              <p className="mt-5 text-lg leading-relaxed text-white/70">AI answer systems have different inputs and changing behaviours. The durable work is not a list of chatbot tricks. It is useful primary content, qualified evidence, sound technical delivery and a clear entity story that helps people and systems understand what you do.</p>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {workstreams.map(({ icon: Icon, title, body }) => (
                <article key={title} className="rounded-xl border border-white/10 bg-black/10 p-5">
                  <Icon className="h-6 w-6 text-[#FF8C00]" aria-hidden="true" />
                  <h3 className="mt-4 text-lg font-semibold">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="container px-4 py-16">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF8C00]">A practical process</p>
              <h2 className="mt-3 font-serif text-3xl">How the citation work runs</h2>
              <p className="mt-5 leading-relaxed text-white/65">The audit is a decision point, not an automatic subscription. If there is a good fit, the monthly plan names the priority pages, evidence owners, work sequence and the measures that will be reviewed.</p>
            </div>
            <ol className="grid gap-4 sm:grid-cols-2">
              {[
                ["01", "Agree the commercial questions", "Choose the buying questions, market and answer surfaces worth investigating."],
                ["02", "Audit the current source base", "Review the pages, evidence, technical delivery and external context that exist today."],
                ["03", "Prioritise the smallest useful scope", "Set the page, content, entity and measurement work that can be owned and checked."],
                ["04", "Measure, review and adjust", "Use a documented query set and technical evidence, rather than presenting one chatbot answer as a result."],
              ].map(([number, title, body]) => (
                <li key={number} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                  <span className="font-serif text-4xl text-[#FF8C00]/60">{number}</span>
                  <h3 className="mt-3 font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#101820] py-16">
          <div className="container px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00D4AA]">Scope and expectations</p>
              <h2 className="mt-3 font-serif text-3xl">What is included, and what is not promised</h2>
            </div>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <article className="rounded-xl border border-[#00D4AA]/25 bg-[#00D4AA]/[0.05] p-6">
                <h3 className="font-serif text-2xl">The work can include</h3>
                <ul className="mt-5 space-y-3 text-sm leading-relaxed text-white/70">
                  {["Priority-question and source baseline", "Answer-first page, content and evidence improvements", "Technical SEO, semantic HTML and structured-data remediation", "Entity, authorship and internal-link consistency", "Documented query checks and agreed reporting cadence"].map((item) => <li key={item} className="flex gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#00D4AA]" aria-hidden="true" />{item}</li>)}
                </ul>
              </article>
              <article className="rounded-xl border border-[#FF8C00]/25 bg-[#FF8C00]/[0.05] p-6">
                <h3 className="font-serif text-2xl">We will not claim</h3>
                <ul className="mt-5 space-y-3 text-sm leading-relaxed text-white/70">
                  {["Guaranteed citation placement or a guaranteed time to citation", "Control over ChatGPT, Gemini, Google or other third-party outputs", "That one prompt response proves durable market visibility", "Unlimited page production outside the agreed scope", "That technical or content work alone creates sales outcomes"].map((item) => <li key={item} className="flex gap-3"><ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#FF8C00]" aria-hidden="true" />{item}</li>)}
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="container px-4 py-16" aria-labelledby="citation-related-heading">
          <h2 id="citation-related-heading" className="font-serif text-3xl">Related guidance and services</h2>
          <div className="mt-7 grid gap-4 md:grid-cols-3">
            {[
              ["/services/seo-aeo-geo/", "SEO, AEO & GEO", "The broader search and answer-engine foundations behind source-ready pages."],
              ["/how-b2b-brands-appear-in-chatgpt-answers/", "How B2B brands appear in ChatGPT answers", "A practical guide to evidence, entity and content conditions worth testing."],
              ["/services/google-ai-mode-optimisation/", "Google AI Mode optimisation", "The adjacent workstream for Google AI search experiences and source visibility."],
            ].map(([href, title, body]) => (
              <Link key={href} href={href} className="rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-[#FF8C00]/60">
                <h3 className="font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">{body}</p>
                <span className="mt-4 inline-block text-sm font-semibold text-[#FFB84D]">Read more →</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="border-t border-white/10 bg-white/[0.03] py-16">
          <div className="container max-w-4xl px-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF8C00]">FAQs</p>
            <h2 className="mt-3 font-serif text-3xl">AI citation service questions</h2>
            <FaqAccordion
              items={faqs}
              headingLevel={3}
              className="mt-8 space-y-3"
              itemClassName="overflow-hidden rounded-lg border border-white/10 bg-[#0F1419]"
              buttonClassName="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.04]"
              questionClassName="m-0 text-base font-semibold text-white"
              answerClassName="px-5 pb-5"
              answerTextClassName="text-sm leading-relaxed text-white/65"
              iconClassName="h-5 w-5 shrink-0 text-[#FF8C00] transition-transform duration-200"
            />
          </div>
        </section>

        <section className="container px-4 py-20 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00D4AA]">A sensible first conversation</p>
          <h2 className="mx-auto mt-3 max-w-3xl font-serif text-3xl md:text-4xl">Start with the buyer question and the evidence you already have.</h2>
          <p className="mx-auto mt-5 max-w-2xl leading-relaxed text-white/65">Bring the pages, the question set and the commercial context. We will tell you whether an audit is useful before recommending any ongoing scope.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer" onClick={bookCall} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#FF8C00] px-6 py-3 text-sm font-bold text-[#0F1419] transition-colors hover:bg-[#FFB84D]">Book a 30-minute call <ArrowRight className="h-4 w-4" /></a>
            <Link href="/contact/" className="inline-flex min-h-11 items-center justify-center rounded-lg border border-white/25 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white/50">Tell us the problem</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
